import {useEffect, useState} from 'react';
import {reportsApi} from '../api/reports.api';
import {getColumnError, getReportFields, readReportColumns} from '../helpers/report-builder.helpers';
import {createReportRunOverrides} from '../helpers/report-run.helpers';
import {ReportEditorColumn, ReportField, ReportTemplate} from '../types/report-builder.types';
import {useReportError} from './use-report-error';

export const useReportRunForm = (report: ReportTemplate) => {
  const [fields, setFields] = useState<ReportField[]>([]);
  const [columns, setColumns] = useState<ReportEditorColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const reportError = useReportError();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const load = async () => {
      try {
        const available = getReportFields(await reportsApi.getFields());
        const selected = readReportColumns(report.body, available).filter((column) => column.isConstant === true);
        if (active) {
          setFields(available);
          setColumns(selected);
        }
      } catch (reason) {
        const details = await reportError(reason, 'Не удалось загрузить поля отчёта.');
        if (active) setError(details.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [report, attempt, reportError]);

  const prepare = () => {
    if (loading || error || !columns.length) return;
    const validation = Object.fromEntries(
      columns.flatMap((column) => {
        const message = getColumnError(
          column,
          fields.find(({key}) => key === column.key),
        );
        return message ? [[column.key, message]] : [];
      }),
    );
    setErrors(validation);
    if (Object.keys(validation).length) return;
    return createReportRunOverrides(columns, fields);
  };

  return {
    fields,
    columns,
    loading,
    error,
    errors,
    prepare,
    retry: () => setAttempt((value) => value + 1),
    change: (column: ReportEditorColumn) => {
      setColumns((current) => current.map((item) => (item.key === column.key ? column : item)));
      setErrors((current) => ({...current, [column.key]: ''}));
    },
  };
};
